import { useCallback, useEffect, useRef, useState } from "react";
import { Device, type Call } from "@twilio/voice-sdk";
import { useVoiceToken } from "@/api/hooks/useVoiceToken";
import { useLogCall } from "@/api/hooks/useInteractions";
import type { CallLogStatus } from "@/api/types/voice";

export type VoiceStatus = "idle" | "connecting" | "oncall" | "error";

export interface UseVoiceResult {
  /** True once GET /voice/token reports Twilio is configured server-side. */
  enabled: boolean;
  /** Still resolving the very first token fetch — the Call button should
   *  stay disabled (not "not set up") while this is true, since we don't
   *  yet know which state we're in. */
  tokenLoading: boolean;
  status: VoiceStatus;
  /** Human-readable message for the "error" status (mic permission denied,
   *  device/connect failure, etc). Null otherwise. */
  error: string | null;
  /** Seconds elapsed since the call was accepted; 0 before/without an
   *  accepted call. */
  elapsedSeconds: number;
  muted: boolean;
  /** Place an outbound call to `toNumber`. `contactId` is carried through
   *  to the call/log POST once the call ends. */
  call: (toNumber: string, contactId: string) => void;
  hangup: () => void;
  mute: (shouldMute: boolean) => void;
  /** Clear an "error" status back to "idle" (e.g. user dismissed the
   *  dialog after a permission-denied message) without affecting a live
   *  call. */
  reset: () => void;
}

const MIC_PERMISSION_MESSAGE =
  "Microphone access is required to make calls. Allow microphone access for this site in your browser settings, then try again.";

/**
 * Manages a single lazily-initialized Twilio Voice SDK `Device` for
 * in-browser (WebRTC) outbound calling — Phase 15. Replaces the Phase 12
 * click-to-call bridge (ring Chris's own phone first, then dial the
 * contact server-side); the call audio now runs entirely through
 * whichever mic/speaker the browser tab has.
 *
 * The `Device` is NOT constructed until the first `call()` — cheap on
 * every ContactDetailPage mount, and means a fresh env with no mic
 * permission granted yet doesn't request it until the user actually
 * tries to call. `enabled`, however, reflects the token endpoint's
 * `enabled` flag immediately (see useVoiceToken), independent of whether
 * the Device has been constructed — that's what gates the Call button.
 *
 * Outbound-only: this hook never calls `device.register()`, so it never
 * receives incoming calls. Chris only originates calls from a contact's
 * detail page.
 */
export function useVoice(): UseVoiceResult {
  const { data: tokenData, isLoading: tokenLoading, refetch: refetchToken } = useVoiceToken();
  const logCall = useLogCall();

  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [muted, setMuted] = useState(false);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const contactIdRef = useRef<string | null>(null);
  const connectedAtRef = useRef<number | null>(null);
  const finalizedRef = useRef(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Always-current refs so Device event handlers (registered once, when the
  // Device is constructed) see the latest token/mutation without having to
  // tear down and rebuild the Device on every render.
  const tokenDataRef = useRef(tokenData);
  tokenDataRef.current = tokenData;
  const refetchTokenRef = useRef(refetchToken);
  refetchTokenRef.current = refetchToken;
  const logCallRef = useRef(logCall);
  logCallRef.current = logCall;

  const stopTicking = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startTicking = useCallback(() => {
    stopTicking();
    connectedAtRef.current = Date.now();
    setElapsedSeconds(0);
    tickRef.current = setInterval(() => {
      const startedAt = connectedAtRef.current;
      if (startedAt == null) return;
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
  }, [stopTicking]);

  const finalize = useCallback(
    (logStatus: CallLogStatus) => {
      if (finalizedRef.current) return;
      finalizedRef.current = true;

      const startedAt = connectedAtRef.current;
      const durationSec = startedAt != null ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : 0;
      const contactId = contactIdRef.current;

      stopTicking();
      connectedAtRef.current = null;
      callRef.current = null;

      if (contactId) {
        // Best-effort: the call already happened from the user's
        // perspective, so a failure to log it should never surface as a
        // call failure — swallow it (loudly, via console) rather than
        // toasting an error over a call that just ended fine.
        logCallRef.current
          .mutateAsync({ contactId, status: logStatus, duration_sec: durationSec })
          .catch((err) => {
            console.error("[voice] failed to log call interaction", err);
          });
      }
    },
    [stopTicking],
  );

  const attachCallHandlers = useCallback(
    (call: Call) => {
      finalizedRef.current = false;

      call.on("accept", () => {
        setStatus("oncall");
        setError(null);
        startTicking();
      });

      call.on("disconnect", () => {
        const wasConnected = connectedAtRef.current != null;
        finalize(wasConnected ? "completed" : "no_answer");
        setStatus("idle");
      });

      call.on("cancel", () => {
        finalize("canceled");
        setStatus("idle");
      });

      call.on("error", (err) => {
        setError(err?.message ?? "The call failed unexpectedly.");
        finalize("failed");
        setStatus("error");
      });
    },
    [finalize, startTicking],
  );

  const ensureDevice = useCallback((token: string): Device => {
    if (deviceRef.current) {
      // Keep an already-constructed Device's token fresh even outside the
      // tokenWillExpire flow (e.g. a fresh mutation resolved a newer
      // token first).
      if (deviceRef.current.token !== token) {
        deviceRef.current.updateToken(token);
      }
      return deviceRef.current;
    }

    const device = new Device(token, { closeProtection: true });
    device.on("tokenWillExpire", () => {
      void refetchTokenRef.current().then((result) => {
        const fresh = result.data?.token;
        if (fresh) device.updateToken(fresh);
      });
    });
    device.on("error", (err) => {
      console.error("[voice] Device error", err);
    });
    deviceRef.current = device;
    return device;
  }, []);

  const call = useCallback(
    (toNumber: string, contactId: string) => {
      setError(null);
      setStatus("connecting");
      setMuted(false);
      contactIdRef.current = contactId;
      finalizedRef.current = false;
      connectedAtRef.current = null;

      if (!Device.isSupported) {
        setStatus("error");
        setError("In-app calling isn't supported in this browser.");
        return;
      }

      const token = tokenDataRef.current?.token;
      if (!tokenDataRef.current?.enabled || !token) {
        setStatus("error");
        setError("In-app calling isn't set up yet.");
        return;
      }

      void (async () => {
        // Request mic permission up front, as its own step, so a denial
        // produces a clear message rather than an opaque Device/Call
        // error further down. We immediately release the probe stream —
        // the Device acquires its own when the call actually connects.
        try {
          const probeStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          probeStream.getTracks().forEach((track) => track.stop());
        } catch {
          setStatus("error");
          setError(MIC_PERMISSION_MESSAGE);
          contactIdRef.current = null;
          return;
        }

        try {
          const device = ensureDevice(token);
          const activeCall = await device.connect({ params: { To: toNumber } });
          callRef.current = activeCall;
          attachCallHandlers(activeCall);
        } catch (err) {
          setStatus("error");
          setError(err instanceof Error ? err.message : "Couldn't start the call.");
        }
      })();
    },
    [attachCallHandlers, ensureDevice],
  );

  const hangup = useCallback(() => {
    callRef.current?.disconnect();
  }, []);

  const mute = useCallback((shouldMute: boolean) => {
    callRef.current?.mute(shouldMute);
    setMuted(shouldMute);
  }, []);

  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
  }, []);

  // Tear down the Device (and any live call) on unmount — a
  // ContactDetailPage navigation away shouldn't leave a WebRTC connection
  // running in the background.
  useEffect(() => {
    return () => {
      stopTicking();
      callRef.current?.disconnect();
      deviceRef.current?.destroy();
      deviceRef.current = null;
    };
  }, [stopTicking]);

  return {
    enabled: tokenData?.enabled ?? false,
    tokenLoading,
    status,
    error,
    elapsedSeconds,
    muted,
    call,
    hangup,
    mute,
    reset,
  };
}
